import { formatLiczby } from '@/lib/i18nNumber';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';

/** Minimal translator shape — avoids pulling i18next's TFunction generics in. */
type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Human label for a numeric range filter, WITHOUT any "∞" / "no max" symbol.
 * Used by the SERP top-bar pills, the active-filter chips AND the slider caption
 * so they all read the same way:
 *   - both bounds → "10–20 m²"
 *   - only min    → "od 10 m²"   (i18n range_from)
 *   - only max    → "do 20 m²"   (i18n range_to)
 *   - neither     → null         (caller shows the field's own name)
 * A bound parked at the slider's extreme is passed as `undefined` (open-ended),
 * which is exactly how the committed filter stores it — so pill/chip/slider stay
 * consistent.
 */
export function formatRange(
  t: Translate,
  min: number | undefined,
  max: number | undefined,
  unit: string,
  /** Język interfejsu — liczby w pastylce mają wyglądać tak samo jak na karcie. */
  lang: string,
): string | null {
  const f = (n: number) => formatLiczby(n, lang);
  if (min && max) return `${f(min)}–${f(max)} ${unit}`;
  if (min) return t('search.filters.range_from', { v: `${f(min)} ${unit}` });
  if (max) return t('search.filters.range_to', { v: `${f(max)} ${unit}` });
  return null;
}

/**
 * ETYKIETA FILTRA CENY — jedna dla pastylki na pulpicie, chipu i skrótu na telefonie.
 * W trybie kosztu MUSI powiedzieć, że mówi o koszcie: „do 4 000 zł" i „do 4 000 zł z
 * opłatami" to dwie różne obietnice, a po zamknięciu arkusza tryb nie jest widoczny
 * nigdzie indziej (zgłoszenie z przeglądu 28.09).
 *
 * CZYSTA FUNKCJA POZA KOMPONENTEM, i to jest poprawka: była funkcją tworzoną na nowo przy
 * każdym renderze, a `activeChips` (useMemo) z niej korzystał, nie mając jej w
 * zależnościach — lint słusznie to zgłosił. Zależy wyłącznie od filtrów i języka, więc
 * te dwie rzeczy są jej jedynymi wejściami.
 */
export function etykietaCenyZ(
  filters: Pick<SourcePostFilters, 'rentMin' | 'rentMax' | 'costMode'>,
  t: Translate,
  jezyk: string,
): { zakres: string; dopisek: string | null } | null {
  const zakres = formatRange(t, filters.rentMin, filters.rentMax, 'zł', jezyk);
  if (!zakres) return null;
  return {
    zakres,
    dopisek: filters.costMode === 'total' ? t('search.filters.cost_mode_total_chip') : null,
  };
}

